import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { User, Loader2, Camera, Save } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';

interface Subject {
  id: string;
  name: string;
  description: string;
}

export default function ProfilePage() {
  const { profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [subjects, setSubjects] = useState<Subject[]>([]);

  const [formData, setFormData] = useState({
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    birth_date: profile?.birth_date || '',
    phone: profile?.phone || '',
    address: profile?.address || '',
    postal_code: profile?.postal_code || '',
    city: profile?.city || '',
    bio: profile?.bio || '',
    subject_id: profile?.subject_id || '',
    teaches_in_institution: profile?.teaches_in_institution || false,
    institution_name: profile?.institution_name || '',
    teaching_experience_years: profile?.teaching_experience_years || 0,
    qualifications: profile?.qualifications || '',
    paypal_merchant_id: profile?.paypal_merchant_id || '',
  });

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        birth_date: profile.birth_date || '',
        phone: profile.phone || '',
        address: profile.address || '',
        postal_code: profile.postal_code || '',
        city: profile.city || '',
        bio: profile.bio || '',
        subject_id: profile.subject_id || '',
        teaches_in_institution: profile.teaches_in_institution || false,
        institution_name: profile.institution_name || '',
        teaching_experience_years: profile.teaching_experience_years || 0,
        qualifications: profile.qualifications || '',
        paypal_merchant_id: profile.paypal_merchant_id || '',
      });
    }
  }, [profile]);

  const fetchSubjects = async () => {
    const { data, error } = await supabase
      .from('subjects')
      .select('*')
      .order('name');

    if (error) {
      console.error('Error fetching subjects:', error);
    } else {
      setSubjects(data || []);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        birth_date: formData.birth_date || null,
        phone: formData.phone,
        address: formData.address,
        postal_code: formData.postal_code,
        city: formData.city,
        bio: formData.bio,
        updated_at: new Date().toISOString(),
      };

      if (profile?.role === 'docente') {
        updateData.subject_id = formData.subject_id || null;
        updateData.teaches_in_institution = formData.teaches_in_institution;
        updateData.institution_name = formData.institution_name;
        updateData.teaching_experience_years = formData.teaching_experience_years;
        updateData.qualifications = formData.qualifications;
        updateData.paypal_merchant_id = formData.paypal_merchant_id;
      }

      const fullName = `${formData.first_name} ${formData.last_name}`.trim();
      if (fullName) {
        updateData.full_name = fullName;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', profile?.id);

      if (error) throw error;

      await refreshProfile();

      toast({
        title: 'Profilo aggiornato',
        description: 'Le tue informazioni sono state salvate con successo!',
      });
    } catch (error: any) {
      toast({
        title: 'Errore',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Profilo
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestisci le tue informazioni personali e professionali
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Foto profilo</CardTitle>
            <CardDescription>
              Carica una foto per il tuo profilo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 bg-blue-600 rounded-full flex items-center justify-center text-white text-3xl font-semibold">
                {profile?.first_name?.[0]?.toUpperCase() || profile?.full_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <Button variant="outline">
                <Camera className="w-4 h-4 mr-2" />
                Carica foto
              </Button>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Dati Personali</CardTitle>
              <CardDescription>
                Informazioni anagrafiche di base
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Nome *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    placeholder="Mario"
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="last_name">Cognome *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    placeholder="Rossi"
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Data di nascita</Label>
                <Input
                  id="birth_date"
                  name="birth_date"
                  type="date"
                  value={formData.birth_date}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Biografia</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  placeholder="Raccontaci qualcosa di te..."
                  rows={4}
                  value={formData.bio}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contatti</CardTitle>
              <CardDescription>
                Email e numero di telefono
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile?.email}
                  disabled
                />
                <p className="text-xs text-gray-500">
                  L'email non può essere modificata
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Numero di telefono</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+39 XXX XXX XXXX"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Residenza</CardTitle>
              <CardDescription>
                Indirizzo di residenza
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  name="address"
                  placeholder="Via Roma, 123"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">CAP</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    placeholder="00100"
                    value={formData.postal_code}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">Città</Label>
                  <Input
                    id="city"
                    name="city"
                    placeholder="Roma"
                    value={formData.city}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {profile?.role === 'docente' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Informazioni Professionali</CardTitle>
                  <CardDescription>
                    Dettagli sulla tua attività di insegnamento
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject_id">Materia di insegnamento</Label>
                    <select
                      id="subject_id"
                      name="subject_id"
                      value={formData.subject_id}
                      onChange={handleChange}
                      disabled={loading}
                      className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="">Seleziona una materia</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Separator />

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Insegni già in un istituto?</Label>
                      <p className="text-sm text-gray-500">
                        Indica se sei già docente presso una scuola o università
                      </p>
                    </div>
                    <Switch
                      checked={formData.teaches_in_institution}
                      onCheckedChange={(checked) => handleSwitchChange('teaches_in_institution', checked)}
                      disabled={loading}
                    />
                  </div>

                  {formData.teaches_in_institution && (
                    <div className="space-y-2">
                      <Label htmlFor="institution_name">Nome istituto</Label>
                      <Input
                        id="institution_name"
                        name="institution_name"
                        placeholder="Es. Liceo Scientifico Galilei"
                        value={formData.institution_name}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="teaching_experience_years">Anni di esperienza nell'insegnamento</Label>
                    <Input
                      id="teaching_experience_years"
                      name="teaching_experience_years"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={formData.teaching_experience_years}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="qualifications">Qualifiche e titoli di studio</Label>
                    <Textarea
                      id="qualifications"
                      name="qualifications"
                      placeholder="Es. Laurea in Matematica, Master in Didattica..."
                      rows={4}
                      value={formData.qualifications}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Pagamenti</CardTitle>
                  <CardDescription>
                    Configurazione per ricevere i pagamenti
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="paypal_merchant_id">PayPal Merchant ID</Label>
                    <Input
                      id="paypal_merchant_id"
                      name="paypal_merchant_id"
                      placeholder="Il tuo PayPal Merchant ID"
                      value={formData.paypal_merchant_id}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500">
                      Necessario per ricevere i pagamenti dei corsi venduti
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Informazioni account</CardTitle>
              <CardDescription>
                Dettagli del tuo account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Ruolo</span>
                  <span className="font-medium capitalize">{profile?.role}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-gray-600">Data registrazione</span>
                  <span className="font-medium">
                    {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('it-IT') : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600">Ultimo aggiornamento</span>
                  <span className="font-medium">
                    {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString('it-IT') : 'N/A'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="submit" disabled={loading} size="lg">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-5 w-5" />
                  Salva modifiche
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
